import React, { useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { CheckCircle, ArrowRight, Phone, Droplets, Shield, Clock, Euro, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";
import ContactModal from "../components/ContactModal";
import Seo from "../components/Seo";
import { serviceJsonLd, filArianeJsonLd, faqJsonLd } from "../utils/donneesStructurees";
import BoutonRappel from "../components/BoutonRappel";
import GuidesLies from "../components/GuidesLies";
import { plafond, formaterEuros, PLAFOND_TRAVAUX } from "../utils/plafonds";
import { listeDepartements } from "../data/departements";
import LienAidesComplementaires from "../components/LienAidesComplementaires";

// Questions affichées ET déclarées en FAQPage : garder les deux synchronisées,
// Google exige que le contenu balisé soit visible sur la page.
//
// L'ancienne version n'était pas balisée et contenait deux erreurs qui se
// retournaient contre nous : elle présentait les cotes PMR comme des normes
// « obligatoires » — elles ne le sont pas dans un logement privé existant, où
// elles servent de référence de bonne pratique — et exigeait des artisans
// « certifiés RGE/Handibat », alors que MaPrimeAdapt' n'impose aucune
// qualification, ce que dit d'ailleurs la page Accès Pro. Le RGE concerne la
// rénovation énergétique, pas l'adaptation.
const QUESTIONS = [
  {
    question: "Combien coûte l'adaptation d'une salle de bain pour une personne âgée ?",
    answer:
      `Le remplacement d'une baignoire par une douche de plain-pied se situe le plus souvent entre 4 000 et 8 000 €, et une salle de bain entièrement adaptée — douche, lavabo accessible, WC surélevé, barres d'appui, sol antidérapant, éclairage — entre 8 000 et 15 000 €. L'écart tient surtout à l'état de la pièce : reprise du sol, déplacement des évacuations, ampleur du carrelage à refaire. Avec MaPrimeAdapt', 70 % de ce montant est pris en charge pour un ménage très modeste et 50 % pour un ménage modeste, dans la limite de ${formaterEuros(PLAFOND_TRAVAUX)} de travaux retenus.`,
  },
  {
    question: "Quelles dimensions prévoir pour une salle de bain accessible ?",
    answer:
      "Dans un logement privé existant, aucune norme ne s'impose à vous : les cotes de référence servent de repère, pas d'obligation. Les plus utiles sont un espace de rotation de 1,50 m pour un fauteuil, une porte d'au moins 90 cm, une douche de 120 × 90 cm sans ressaut, un lavabo dont le plan reste sous 85 cm avec un dégagement en dessous, et un sol classé antidérapant. Dans une pièce étroite, on ne les atteint pas toutes — la visite d'évaluation sert justement à arbitrer ce qui compte le plus pour vous.",
  },
  {
    question: "Faut-il faire appel à un artisan RGE ou labellisé Handibat ?",
    answer:
      "Non. MaPrimeAdapt' n'exige aucune qualification particulière de l'entreprise qui réalise les travaux : le label RGE concerne la rénovation énergétique, pas l'adaptation du logement. Handibat et Silverbat sont des signes de spécialisation utiles, mais ils ne conditionnent pas l'aide. Ce que l'ANAH demande, c'est un devis détaillé d'une entreprise en règle, cohérent avec le diagnostic d'autonomie du dossier.",
  },
  {
    question: "Combien de temps durent les travaux, et le logement reste-t-il habitable ?",
    answer:
      "Le chantier lui-même dure généralement de 3 à 6 jours pour un remplacement de baignoire, une à deux semaines pour une réfection complète. La salle de bain est inutilisable pendant la dépose et le temps de séchage, soit deux à trois jours : c'est le point à anticiper quand la personne vit seule. Le calendrier d'ensemble est plus long que le chantier — comptez deux à trois mois d'instruction ANAH avant de pouvoir démarrer, car des travaux commencés avant l'accord écrit font perdre l'aide.",
  },
  {
    question: "Qui peut en bénéficier en Île-de-France ?",
    answer:
      `Il faut avoir 70 ans ou plus, ou de 60 à 69 ans avec une perte d'autonomie reconnue (GIR 1 à 6), ou être en situation de handicap avec un taux d'incapacité d'au moins 50 %. Le logement doit être votre résidence principale, comme propriétaire occupant ou comme locataire du parc privé avec l'accord du bailleur. Les plafonds de ressources franciliens sont plus élevés que les plafonds nationaux : ${formaterEuros(plafond("tresModestes", 1))} de revenu fiscal de référence pour une personne seule au titre des ménages très modestes, ${formaterEuros(plafond("modestes", 1))} pour les ménages modestes.`,
  },
];

const SOLUTIONS_DOUCHE = [
  {
    titre: "Douche de plain-pied, sans aucun ressaut",
    texte:
      "Le receveur est encastré dans le sol, ou remplacé par un sol coulé en pente douce. Rien à enjamber, un fauteuil roulant peut entrer. C'est la solution la plus durable, mais elle suppose de descendre l'évacuation dans le plancher : simple en rez-de-chaussée sur vide sanitaire, plus délicat sur une dalle béton d'immeuble.",
    retenir:
      "à privilégier si l'autonomie va continuer à baisser, ou si un fauteuil est déjà envisagé.",
  },
  {
    titre: "Receveur extra-plat, ressaut de 2 à 3 cm",
    texte:
      "Le receveur se pose sur le sol existant sans toucher à l'évacuation. Le chantier est plus court et moins cher, et le ressaut résiduel de deux à trois centimètres se franchit sans difficulté avec un déambulateur. C'est le compromis que nous retenons le plus souvent en appartement.",
    retenir:
      "le meilleur rapport résultat/contraintes quand le plancher ne peut pas être creusé.",
  },
  {
    titre: "Cabine sécurisée à porte étanche",
    texte:
      "Une cabine préfabriquée avec une porte qui s'ouvre, un siège intégré et des barres d'appui. Elle s'installe là où la maçonnerie interdit tout le reste, et rassure les personnes qui craignent de glisser. En contrepartie, elle est encombrante et son remplacement est un chantier en soi.",
    retenir:
      "utile en dernier recours, ou quand la personne ne tient pas debout longtemps.",
  },
];

const SalleDeBain = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const departements = listeDepartements();

  const benefits = [
    "Sécurité renforcée avec surfaces antidérapantes",
    "Accessibilité optimisée pour tous les âges", 
    "Confort d'utilisation au quotidien",
    "Valorisation de votre bien immobilier",
    "Économies d'énergie avec nouveaux équipements",
    "Maintenance réduite avec matériaux durables"
  ];

  // Fourchettes alignées sur celles de la FAQ ci-dessus : la page annonçait
  // 8 500 - 15 000 € ici et 2 500 - 8 000 € dans sa FAQ pour exactement le même
  // travail. Un visiteur qui lisait les deux ne pouvait que douter du reste.
  // À remplacer par les montants réellement constatés sur nos dossiers, qui
  // seront à la fois justes et défendables.
  const solutions = [
    {
      title: "Remplacement baignoire par douche",
      description: "Douche de plain-pied avec receveur extra-plat antidérapant",
      price: "4 000 - 8 000 €",
      duration: "3-6 jours"
    },
    {
      title: "Adaptation salle de bain complète",
      description: "Réaménagement global pour l'accessibilité et la sécurité",
      price: "8 000 - 15 000 €",
      duration: "1-2 semaines"
    },
    {
      title: "Équipements de sécurité",
      description: "Barres d'appui, siège de douche, robinetterie adaptée",
      price: "1 500 - 3 500 €",
      duration: "1 jour"
    }
  ];

  const process = [
    {
      step: "1",
      title: "Diagnostic à domicile",
      description: "Visite gratuite de notre visiteur spécialiste pour évaluer vos besoins et mesurer l'espace"
    },
    {
      step: "2", 
      title: "Recherche entreprise",
      description: "Nous vous orientons vers une entreprise qui viendra faire le devis"
    },
    {
      step: "3",
      title: "Montage dossier", 
      description: "Constitution du dossier MaPrimeAdapt' avec le devis entreprise"
    },
    {
      step: "4",
      title: "Réalisation travaux",
      description: "L'entreprise sélectionnée réalise les travaux d'adaptation",
      duration: "2-4 mois"
    },
    {
      step: "5",
      title: "Paiement aides",
      description: "Versement des aides MaPrimeAdapt' sur votre compte après achèvement",
      duration: "2-4 semaines"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Seo
        titre="Douche et salle de bain adaptées — aides MaPrimeAdapt'"
        description="Douche sécurisée, barres d'appui, sol antidérapant financés à 70 % par MaPrimeAdapt'. AMO agréé ANAH en IDF. Simulation gratuite en 2 minutes. Réponse sous 24 h."
        chemin="/salle-de-bain"
        donnees={[
          serviceJsonLd({
            nom: "Adaptation de salle de bain pour senior et PMR",
            description: "Douche sécurisée de plain-pied, barres d'appui, sol antidérapant et siège de douche, financés par MaPrimeAdapt'.",
            url: "/salle-de-bain",
          }),
          filArianeJsonLd([
            { nom: "Accueil", url: "/" },
            { nom: "Salle de bain adaptée", url: "/salle-de-bain" },
          ]),
          faqJsonLd(QUESTIONS),
        ]}
      />
      <Header />

      <main id="contenu">
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 via-white to-blue-50 py-20 overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'url("/images/fond-salle-de-bain.webp")',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-r from-blue-50/90 to-white/90"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <div className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                  <Droplets className="h-4 w-4 mr-2" />
                  Adaptation salle de bain • MaPrimeAdapt'
                </div>
                
                <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
                  Douche sénior et salle de bain PMR en Île-de-France{" "}
                  <span className="text-blue-600 block mt-2">financées par MaPrimeAdapt' 2026</span>
                </h1>
                
                <p className="text-xl text-gray-700 leading-relaxed">
                  <strong>Spécialiste adaptation salle de bain PMR en France</strong> : Nous montons votre dossier MaPrimeAdapt' 
                  pour transformer votre baignoire en douche sécurisée aux normes accessibilité. 
                  Bénéficiez jusqu'à <strong className="text-blue-600">70% d'aides financières</strong> avec nos artisans certifiés.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/simulateur">
                  <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white shadow-lg group w-full sm:w-auto">
                    Tester mon éligibilité
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-blue-200 hover:bg-blue-50 w-full sm:w-auto"
                  onClick={() => setIsContactModalOpen(true)}
                >
                  <MessageCircle className="mr-2 h-5 w-5" />
                  Nous contacter
                </Button>
                
                <BoutonRappel className="border-2 border-blue-200 hover:bg-blue-50 w-full sm:w-auto" />
              </div>

              <div className="grid grid-cols-3 gap-6 pt-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">2-6 jours</div>
                  <div className="text-sm text-gray-700">Installation</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">70%</div>
                  <div className="text-sm text-gray-700">D'aides possibles</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">15 ans</div>
                  <div className="text-sm text-gray-700">Garantie</div>
                </div>
              </div>
            </div>

            {/* Example Box with Image */}
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100">
              <img 
                src="/images/salle-bain.webp"
                width="1170"
                height="862"
                loading="eager" 
                alt="Transformation salle de bain - Avant baignoire / Après douche accessible" 
                className="w-full h-80 object-cover object-bottom"
              />
              <div className="p-4">
                <h2 className="text-lg font-semibold text-gray-900 mb-3">
                  Exemple Mme Dubois : Adaptation salle de bain
                </h2>
              
              <div className="space-y-2">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-700">Coût total des travaux</span>
                  <span className="font-semibold text-gray-900">15 630 €</span>
                </div>
                
                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-green-700">MaPrimeAdapt' (70%)</span>
                    <span className="font-semibold text-green-700">- 10 941 €</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-green-700">AMO (pris en charge)</span>
                    <span className="font-semibold text-green-700">0 €</span>
                  </div>
                </div>

                <div className="pt-2 border-t-2 border-blue-200">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-gray-900">Reste à charge</span>
                    <span className="text-xl font-bold text-gray-400 line-through decoration-2 decoration-orange-400">4 689 €</span>
                  </div>
                </div>
                <LienAidesComplementaires />
                
                <div className="bg-blue-50 p-2 rounded-lg mt-2">
                  <p className="text-xs text-blue-800">
                    <strong>Économie totale :</strong> 10 941€ grâce à notre accompagnement MaPrimeAdapt'
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
      </section>

      {/* Image supprimée comme demandé */}

      {/* Solutions Section */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Salle de bain PMR : les équipements financés et les cotes de référence
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              <strong>AMO agréé ANAH</strong> : nous accompagnons les seniors et les personnes
              en situation de handicap dans l&apos;adaptation de leur salle de bain. Dossier
              MaPrimeAdapt&apos;, devis, suivi de chantier, sur Paris et cinq départements
              franciliens.
            </p>
          </div>

          {/* Contenu informatif inspiré de Logiadapt */}
          <div className="mb-16">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-12">
              <h3 className="text-xl font-semibold text-red-800 mb-4">
                ⚠️ Dans la salle de bain : la pièce la plus dangereuse
              </h3>
              <p className="text-red-700 leading-relaxed">
                C'est la pièce la plus dangereuse de votre domicile. Indispensable pour l'hygiène de toute personne, 
                le fait qu'elle soit très souvent mouillée accroît les risques de chute chez les seniors.
              </p>
            </div>

            {/* Section explicative : Qu'est-ce qu'une salle de bain PMR */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-12">
              <h3 className="text-xl font-semibold text-blue-800 mb-4">
                Salle de bain PMR : ce que le terme recouvre
              </h3>
              {/* La version précédente présentait ces cotes comme une
                  « réglementation » opposable et citait la loi ELAN, qui régit
                  la construction neuve et les parties communes, pas
                  l'adaptation d'un logement privé existant. Chez vous, ces
                  dimensions sont des repères de bonne pratique. */}
              <p className="text-blue-700 leading-relaxed mb-4">
                Une <strong>salle de bain PMR</strong> (personnes à mobilité réduite) reprend
                les cotes d&apos;accessibilité utilisées dans la construction neuve : douche de
                plain-pied, barres d&apos;appui, lavabo dégagé en dessous, sol antidérapant.
                Dans un logement privé existant, ces dimensions ne s&apos;imposent pas à vous —
                elles servent de référence pour un aménagement réellement utilisable, et
                l&apos;ANAH ne conditionne pas l&apos;aide à leur respect intégral. Dans une
                pièce ancienne et étroite, on retient celles qui changent la vie quotidienne
                de l&apos;occupant.
              </p>
              
              <div className="grid md:grid-cols-2 gap-6 mt-6">
                <div>
                  <h4 className="font-semibold text-blue-800 mb-3">🔧 Cotes de référence :</h4>
                  <ul className="text-sm text-blue-700 space-y-2">
                    <li>• <strong>Espace de rotation :</strong> 150 cm de diamètre minimum</li>
                    <li>• <strong>Largeur de porte :</strong> 90 cm minimum</li>
                    <li>• <strong>Hauteur lavabo :</strong> 85 cm maximum</li>
                    <li>• <strong>Douche accessible :</strong> 120 x 90 cm minimum</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-blue-800 mb-3">✅ Équipements les plus utiles :</h4>
                  <ul className="text-sm text-blue-700 space-y-2">
                    <li>• Barres d'appui robustes</li>
                    <li>• Siège de douche rabattable</li>
                    <li>• Sol antidérapant certifié</li>
                    <li>• Robinetterie accessible (90-130 cm)</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Section avantages d'une salle de bain PMR */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-12">
              <h3 className="text-xl font-semibold text-green-800 mb-4">
                Pourquoi installer une salle de bain PMR ?
              </h3>
              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <div className="text-green-700 text-2xl mb-2">🛡️</div>
                  <h3 className="font-semibold text-green-800 mb-2">Sécurité renforcée</h3>
                  <p className="text-sm text-green-700">Réduction drastique des risques de chutes grâce aux équipements adaptés et aux surfaces antidérapantes.</p>
                </div>
                <div>
                  <div className="text-green-700 text-2xl mb-2">🚀</div>
                  <h3 className="font-semibold text-green-800 mb-2">Autonomie améliorée</h3>
                  <p className="text-sm text-green-700">Utilisation confortable et sans aide extérieure grâce aux installations spécifiquement adaptées.</p>
                </div>
                <div>
                  <div className="text-green-700 text-2xl mb-2">💎</div>
                  <h3 className="font-semibold text-green-800 mb-2">Confort au quotidien</h3>
                  <p className="text-sm text-green-700">Design moderne et fonctionnel alliant esthétique et praticité pour un bien-être optimal.</p>
                </div>
              </div>
            </div>

            {/* Grid 2x2 des adaptations avec vignettes agrandies */}
            <div className="grid md:grid-cols-2 gap-8">
              {/* Douche sécurisée de plain-pied */}
              <div className="bg-red-50 p-6 rounded-lg border border-red-200">
                <div className="flex items-start space-x-6">
                  <div className="w-32 h-32 bg-white rounded-lg flex items-center justify-center flex-shrink-0 p-3 shadow-md">
                    <img 
                      src="/images/douche-securisee.webp"
                      alt="Douche de plain-pied avec receveur extra-plat"
                      width="320"
                      height="320"
                      loading="lazy"
                      className="w-full h-full object-cover rounded"
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-red-700 mb-3">
                      Douche sécurisée de plain-pied
                    </h3>
                    <p className="text-sm text-gray-700 leading-relaxed mb-3">
                      Transformer sa baignoire en douche de plain-pied ou installer une douche à l'italienne 
                      fait partie des indispensables de l'adaptation du logement. En évitant les marches, 
                      on garantit un accès sécurisé pour se laver.
                    </p>
                    <div className="bg-red-100 p-3 rounded text-xs text-red-800">
                      <strong>Cotes de référence :</strong> douche 120 x 90 cm • receveur extra-plat •
                      robinetterie entre 90 et 130 cm • assise du siège entre 45 et 50 cm
                    </div>
                  </div>
                </div>
              </div>

              {/* Réhausse des toilettes */}
              <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                <div className="flex items-start space-x-6">
                  <div className="w-32 h-32 bg-white rounded-lg flex items-center justify-center flex-shrink-0 p-3 shadow-md">
                    <img 
                      src="/images/wc-rehausse.webp"
                      alt="WC rehaussé avec barre d'appui"
                      width="320"
                      height="320"
                      loading="lazy"
                      className="w-full h-full object-cover rounded"
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-blue-700 mb-3">
                      Réhausse des toilettes
                    </h3>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      Lorsqu'on vieillit, il peut s'avérer difficile de se baisser et de se relever. 
                      Réhausser les WC est une solution pratique qui permet de gagner en autonomie 
                      et en confort au quotidien.
                    </p>
                  </div>
                </div>
              </div>

              {/* Adaptation du lavabo */}
              <div className="bg-red-50 p-6 rounded-lg border border-red-200">
                <div className="flex items-start space-x-6">
                  <div className="w-32 h-32 bg-white rounded-lg flex items-center justify-center flex-shrink-0 p-3 shadow-md">
                    <img 
                      src="/images/lavabo-adapte.webp"
                      alt="Lavabo adapté accessible en position assise"
                      width="320"
                      height="320"
                      loading="lazy"
                      className="w-full h-full object-cover rounded"
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-red-700 mb-3">
                      Adaptation du lavabo
                    </h3>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      Pour rendre plus facile et plus sécurisé l'accès au lavabo, on privilégiera 
                      les lavabos autoportants, aux larges rebords, avec barre de maintien ou 
                      support mural réglable en hauteur.
                    </p>
                  </div>
                </div>
              </div>

              {/* Revêtement anti-dérapant */}
              <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                <div className="flex items-start space-x-6">
                  <div className="w-32 h-32 bg-white rounded-lg flex items-center justify-center flex-shrink-0 p-3 shadow-md">
                    <img 
                      src="/images/sol-antiderapant.webp"
                      alt="Sol antidérapant de salle de bain"
                      width="320"
                      height="320"
                      loading="lazy"
                      className="w-full h-full object-cover rounded"
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-blue-700 mb-3">
                      Revêtement anti-dérapant
                    </h3>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      La pose d'un carrelage ou d'un revêtement de sol antidérapant est cruciale 
                      pour limiter le risque de chute dans la salle de bain. Il convient également 
                      de choisir un tapis de bain antidérapant.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {solutions.map((solution, index) => (
              <Card key={index} className="border-2 hover:border-blue-200 transition-all duration-300 hover:shadow-lg">
                <CardHeader>
                  <CardTitle className="text-xl text-gray-900 mb-2">
                    {solution.title}
                  </CardTitle>
                  <CardDescription className="text-gray-700">
                    {solution.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center">
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      <Euro className="h-3 w-3 mr-1" />
                      {solution.price}
                    </Badge>
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                      <Clock className="h-3 w-3 mr-1" />
                      {solution.duration}
                    </Badge>
                  </div>
                  
                  <Link to="/simulateur">
                    <Button className="w-full bg-blue-600 hover:bg-blue-700">
                      Obtenir un devis
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Benefits */}
          <div className="bg-white rounded-2xl p-8 lg:p-12 shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
              Pourquoi adapter votre salle de bain ?
            </h3>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-blue-500 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Comment ça marche ?
            </h2>
            <p className="text-xl text-gray-700 max-w-2xl mx-auto">
              Un accompagnement personnalisé de A à Z pour votre projet de salle de bain
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-8">
            {process.map((step, index) => (
              <div key={index} className="text-center">
                <div className="relative mb-6">
                  <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                    {step.step}
                  </div>
                  {index < 4 && (
                    <div className="hidden lg:block absolute top-8 left-full w-full h-0.5 bg-blue-200 transform -translate-y-1/2"></div>
                  )}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-gray-700 text-sm">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h3 className="text-2xl lg:text-3xl font-bold mb-4">
            Prêt à transformer votre salle de bain ?
          </h3>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Bénéficiez de notre expertise et des aides MaPrimeAdapt' pour votre projet
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-lg mx-auto">
            <Link to="/simulateur" className="w-full sm:w-auto">
              <Button 
                size="lg" 
                className="bg-white text-blue-600 hover:bg-gray-100 w-full sm:w-auto shadow-lg"
              >
                Démarrer ma simulation
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-2 border-white text-white hover:bg-white hover:text-green-700 w-full sm:w-auto"
              onClick={() => setIsContactModalOpen(true)}
            >
              <MessageCircle className="mr-2 h-5 w-5" />
              Nous contacter
            </Button>
            
            <Button 
              size="lg" 
              variant="outline" 
              className="border-2 border-white text-white hover:bg-white hover:text-blue-600 w-full sm:w-auto"
              onClick={() => window.location.href = 'tel:0939035444'}
            >
              <Phone className="mr-2 h-5 w-5" />
              09 39 03 54 44
            </Button>
          </div>
        </div>
      </section>

      {/* Choix de la douche : la décision concrète que le visiteur a en tête
          quand il tape « douche sénior », et que le reste de la page
          n'abordait nulle part. */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Douche sénior : quelle solution pour quelle salle de bain
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-10">
              Remplacer une baignoire est le travail le plus demandé de nos dossiers, et
              celui où l&apos;on hésite le plus. Trois solutions reviennent, et le choix
              tient moins au budget qu&apos;à la configuration de la pièce et à
              l&apos;évolution prévisible de l&apos;autonomie.
            </p>

            <div className="space-y-6">
              {SOLUTIONS_DOUCHE.map((s) => (
                <div key={s.titre} className="border-l-4 border-blue-600 bg-blue-50 rounded-r-xl p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{s.titre}</h3>
                  <p className="text-gray-700 leading-relaxed mb-3">{s.texte}</p>
                  <p className="text-gray-900">
                    <strong>À retenir :</strong> {s.retenir}
                  </p>
                </div>
              ))}
            </div>

            <p className="text-gray-700 leading-relaxed mt-10">
              Un point revient systématiquement pendant la visite : faut-il conserver une
              baignoire pour le reste de la famille ? Dans une salle de bain unique, la
              réponse est presque toujours non. L&apos;enjambement d&apos;un rebord de
              baignoire est le geste qui provoque le plus de chutes graves à domicile, et
              une douche de plain-pied reste utilisable par tout le monde, à tout âge.
            </p>
          </div>
        </div>
      </section>

      {/* Maillage départemental : les aides locales changent le reste à charge,
          et les requêtes du type « salle de bain PMR 91 » passent par là. */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">
              Votre reste à charge dépend aussi de votre département
            </h2>
            <p className="text-lg text-gray-700 mb-8">
              MaPrimeAdapt&apos; se cumule avec les aides de votre département, qui ne sont
              pas les mêmes d&apos;un bout à l&apos;autre de l&apos;Île-de-France. Les
              Hauts-de-Seine versent une aide forfaitaire, la Seine-Saint-Denis un
              pourcentage des travaux, Paris fonctionne au cas par cas. Sur un même devis
              de douche, l&apos;écart se compte en centaines d&apos;euros.
            </p>
            <ul className="flex flex-wrap justify-center gap-3">
              {departements.map((d) => (
                <li key={d.code}>
                  <Link
                    to={`/amo-anah-${d.cle}-${d.code}`}
                    className="inline-block px-4 py-3 bg-white border-2 border-blue-100 rounded-lg text-gray-900 font-medium hover:border-blue-600 hover:bg-blue-50 transition-colors"
                  >
                    Salle de bain PMR {d.ou} ({d.code})
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FAQ — les mêmes questions sont déclarées en FAQPage plus haut */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              Adapter sa salle de bain avec MaPrimeAdapt&apos; : vos questions
            </h2>

            <div className="space-y-6">
              {QUESTIONS.map((item) => (
                <div key={item.question} className="bg-white p-6 rounded-lg shadow-md">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    {item.question}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">{item.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <GuidesLies slugs={["douche-senior-normes-et-aides", "reste-a-charge-adaptation-logement"]} />

      </main>

      <Footer />
      
      {/* Contact Modal */}
      <ContactModal 
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        type="general"
      />
    </div>
  );
};

export default SalleDeBain;